'use client';

import { useState } from 'react';

import { FeatureProductInfoMain } from '@/fo/feature/product/info/main';
import { FeatureProductTileMedium } from '@/fo/feature/product/tile/medium';
import { UiKitBreadcrumbsBase } from '@/fo/ui/kit/breadcrumbs/base';
import { UiKitLabelBrand } from '@/fo/ui/kit/label/brand';
import { UiProductInfoCarePrograms } from '@/fo/ui/product/info/care-programs';
import { UiProductInfoGroupedComposition } from '@/fo/ui/product/info/grouped-composition';
import { UiProductInfoPanel } from '@/fo/ui/product/info/panel';
import { UiProductInfoTop } from '@/fo/ui/product/info/top';
import { UiProductLayoutPage } from '@/fo/ui/product/layout/page';
import { UiProductPictureMain } from '@/fo/ui/product/picture/main';
import { Product } from '@/fo/ui/product/tile/medium';
import {
  BreadcrumbsItem,
  getProductH1,
  MappedProduct
} from '@/fo/util/map-product-data';

import { styles } from './ProductPageClient.styles';

export interface ProductPageClientProps {
  breadcrumbs?: BreadcrumbsItem[];
  product: MappedProduct;
  productId: string;
  isAuthorized: boolean;
  loyaltyLevel: number;
  maxStockQuantity: number;
  programsBrief: Product[];
  slots: {
    advantages: React.ReactNode;
    delivery: React.ReactNode;
    banners: React.ReactNode;
  };
}

export function ProductPageClient({
  breadcrumbs,
  product,
  productId,
  isAuthorized,
  loyaltyLevel,
  programsBrief,
  maxStockQuantity,
  slots
}: ProductPageClientProps) {
  const stylesSlots = styles();

  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <div className={stylesSlots.base()}>
      <UiProductLayoutPage
        slots={{
          top: (
            <div className="flex w-full items-center justify-between">
              {breadcrumbs && (
                <UiKitBreadcrumbsBase
                  className="max-md:pt-[10px] max-md:pb-[2px] lg:hidden"
                  items={breadcrumbs}
                  small="lg"
                  isOutputSchemaJson={false}
                />
              )}
              <UiProductInfoTop
                className="lg:w-full"
                url={`product/${productId}`}
                rating={product.rating}
              />
            </div>
          ),
          picture: (
            <UiProductPictureMain
              tags={product.labels}
              images={product.mediaGallery}
              productH1={getProductH1(product)}
            />
          ),
          main: (
            <FeatureProductInfoMain
              isAuthorized={isAuthorized}
              listName="product-detail-page"
              goToDescription={() => setActiveIndex(0)}
              labels={
                product.labelsBrand.length
                  ? product.labelsBrand.map(({ name, color, url }) => (
                      <UiKitLabelBrand
                        key={name}
                        url={url}
                        color={color}
                      >
                        {name}
                      </UiKitLabelBrand>
                    ))
                  : null
              }
              maxStockQuantity={maxStockQuantity}
              product={product}
              loyaltyLevel={loyaltyLevel}
            />
          ),
          recommends: (
            <div>
              {Boolean(programsBrief.length) && (
                <UiProductInfoCarePrograms products={programsBrief} />
              )}
              {product.items && (
                <UiProductInfoGroupedComposition products={product.items}>
                  {product.items.map((item, index) => (
                    <FeatureProductTileMedium
                      key={`${item.sku}-${index}`}
                      data={item}
                    />
                  ))}
                </UiProductInfoGroupedComposition>
              )}
            </div>
          ),
          delivery: slots.delivery,
          advantages: slots.advantages,
          banners: slots.banners
        }}
      />
      <UiProductInfoPanel
        product={product}
        activeIndex={activeIndex}
        onTabChange={setActiveIndex}
      />
    </div>
  );
}
